import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChartBarIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  StarIcon,
  ClipboardDocumentCheckIcon,
  CheckBadgeIcon,
  CalendarIcon,
  LockClosedIcon,
  CloudArrowUpIcon,
  InboxStackIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const PerformanceDashboard = () => {
  const [systemStatus, setSystemStatus] = useState('GOALS_ASSIGNED');
  const [activeTab, setActiveTab] = useState('appraisal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [compensation, setCompensation] = useState(null);
  const [cycleInfo, setCycleInfo] = useState(null);
  
  const empCode = localStorage.getItem('emp_code');
  const empName = localStorage.getItem('emp_name') || 'Employee';
  const empDesignation = localStorage.getItem('designation') || 'Staff';
  const [cycleId, setCycleId] = useState(null);
  const userRole = localStorage.getItem('role') || 'employee';

  const [ringsVisible, setRingsVisible] = useState(false);
  const [goals, setGoals] = useState([]);

  useEffect(() => {
    if (!empCode) {
      setError('Employee code not found. Please login again.');
      setLoading(false);
      return;
    }
    fetchActiveCycle();
    const timer = setTimeout(() => setRingsVisible(true), 600);
    return () => clearTimeout(timer);
  }, []);

  const fetchActiveCycle = async () => {
    try {
      const response = await fetch('http://localhost:8000/api/appraisal/active-cycle');
      if (!response.ok) throw new Error('No active appraisal cycle found');
      
      const data = await response.json();
      setCycleId(data.cycle_id);
      setCycleInfo(data);
      fetchPerformanceData(data.cycle_id);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchPerformanceData = async (activeCycleId) => {
    try {
      setLoading(true);
      const response = await fetch(`http://localhost:8000/api/performance-review/${empCode}/${activeCycleId}`);
      if (!response.ok) throw new Error('Failed to fetch performance data');
      
      const data = await response.json();
      setGoals(data.goals || []);
      setSystemStatus(data.systemStatus || 'GOALS_ASSIGNED');
      setCompensation(data.compensation);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (id, field, val) => {
    setGoals(goals.map(g => g.id === id ? { ...g, [field]: val } : g));
  };

  const submitSelfAppraisal = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`http://localhost:8000/api/performance-review/submit-self/${empCode}/${cycleId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ goals })
      });
      
      if (!response.ok) throw new Error('Failed to submit self review');
      
      const result = await response.json();
      setSystemStatus(result.status);
      alert('Self review submitted successfully!');
    } catch (err) {
      alert('Error submitting review: ' + err.message);
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitManagerReview = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`http://localhost:8000/api/performance-review/submit-manager/${empCode}/${cycleId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ goals })
      });
      
      if (!response.ok) throw new Error('Failed to submit manager review');
      
      const result = await response.json();
      setSystemStatus(result.status);
      alert('Manager review submitted successfully!');
    } catch (err) {
      alert('Error submitting review: ' + err.message);
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Data for charts
  const barChartData = {
    labels: goals.map(g => g.criteria),
    datasets: [
      {
        label: 'Self Rating',
        data: goals.map(g => parseFloat(g.selfRating) || 0),
        backgroundColor: 'rgba(99, 102, 241, 0.65)',
        borderColor: '#6366f1',
        borderWidth: 1,
      },
      {
        label: 'Manager Rating',
        data: goals.map(g => parseFloat(g.managerRating) || 0),
        backgroundColor: 'rgba(16, 185, 129, 0.65)',
        borderColor: '#10b981',
        borderWidth: 1,
      }
    ]
  };

  const pieChartData = {
    labels: goals.map(g => g.criteria),
    datasets: [{
      data: goals.map(g => parseInt(g.weight) || 0),
      backgroundColor: ['#6366f1', '#10b981', '#f59e0b', '#8b5cf6'],
      borderColor: '#ffffff',
      borderWidth: 2,
    }]
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <ArrowPathIcon className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading performance data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 border border-red-200 shadow-sm max-w-md">
          <div className="text-center">
            <div className="h-16 w-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <InboxStackIcon className="h-8 w-8" />
            </div>
            <h3 className="font-bold text-slate-800 text-lg mb-2">Error Loading Data</h3>
            <p className="text-sm text-slate-600 mb-4">{error}</p>
            <button 
              onClick={fetchActiveCycle}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg text-sm font-medium"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8 font-roboto text-slate-800">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@600;700&family=Roboto:wght@400;500;700&display=swap');
        .font-heading { font-family: 'Poppins', sans-serif; }
      `}</style>

      <div className="max-w-7xl mx-auto">
        {/* TOP HEADER & PROFILE */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-6">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-100">
              <UserCircleIcon className="h-10 w-10 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold font-heading text-slate-900">{cycleInfo?.year || 'Annual Appraisal'}</h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-[10px] bg-slate-200 px-2 py-0.5 rounded font-bold text-slate-600 uppercase tracking-tighter">{empCode}</span>
                <span className="text-xs font-medium text-slate-500 italic font-roboto">{empName} • {empDesignation}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 bg-white p-2 rounded-2xl border border-slate-200 shadow-sm">
            <div className="text-right px-4">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Cycle Status</p>
              <p className="text-xs font-bold text-indigo-600 uppercase">{systemStatus.replace('_', ' ')}</p>
            </div>
            <div className="h-10 w-[1px] bg-slate-100"></div>
            <div className="flex items-center gap-2 px-4 text-rose-500 font-bold text-xs">
              <CalendarIcon className="h-5 w-5" /> 20 JAN
            </div>
          </div>
        </div>

        {/* PROGRESS STEPPER */}
        <div className="bg-white rounded-2xl p-8 mb-8 border border-slate-200 shadow-sm">
          <div className="flex justify-between items-center max-w-4xl mx-auto relative">
            <div className="absolute h-0.5 w-full bg-slate-100 top-[15px] z-0"></div>
            {['GOALS_ASSIGNED', 'SELF_SUBMITTED', 'MANAGER_REVIEWED', 'RELEASED'].map((step, i) => (
              <div key={step} className="z-10 flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold transition-all duration-500 border-4 ${
                  systemStatus === step ? 'bg-indigo-600 text-white border-indigo-100 scale-125' : 'bg-white border-slate-100 text-slate-300'
                }`}>
                  {i + 1}
                </div>
                <span className={`text-[9px] mt-3 font-bold uppercase tracking-widest ${systemStatus === step ? 'text-indigo-600' : 'text-slate-300'}`}>
                  {step.split('_')[0]}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* LEFT SIDE: THE FORM */}
          <div className="lg:col-span-8">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/30">
                <div className="flex items-center gap-2">
                  <ClipboardDocumentCheckIcon className="h-5 w-5 text-indigo-600" />
                  <h2 className="font-heading font-bold text-slate-800 text-sm">Appraisal Assessment Form</h2>
                </div>
                {(systemStatus === 'MANAGER_REVIEWED' || systemStatus === 'RELEASED') && (
                  <div className="flex items-center gap-1.5 bg-amber-50 px-3 py-1 rounded-full text-[10px] font-bold text-amber-600 border border-amber-100">
                    <LockClosedIcon className="h-3 w-3" /> FORM LOCKED
                  </div>
                )}
              </div>

              <div className="p-6 space-y-10">
                {goals.map((goal) => (
                  <div key={goal.id} className="relative group border border-slate-100 rounded-xl p-6 bg-slate-50/30">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                      <div>
                        <h3 className="font-bold text-slate-900 font-heading text-lg">{goal.criteria}</h3>
                        <p className="text-xs text-slate-400 mt-1 font-medium italic">Weight: {goal.weight}</p>
                      </div>
                    </div>

                    {/* Employee Submission Section */}
                    <div className="bg-white rounded-lg p-4 mb-4 border border-slate-200">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                          <UserCircleIcon className="h-4 w-4 text-indigo-600" />
                          Employee review
                        </h4>
                        <div>
                          <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Self Rating</label>
                          <select
                            disabled={systemStatus !== 'GOALS_ASSIGNED'}
                            value={goal.selfRating}
                            onChange={(e) => handleUpdate(goal.id, 'selfRating', e.target.value)}
                            className="w-32 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm font-bold focus:ring-2 focus:ring-indigo-500 outline-none disabled:bg-slate-100"
                          >
                            <option value="">Select Rating</option>
                            {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}.0</option>)}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Key Achievements & Comments</label>
                        <textarea
                          placeholder="Describe your key achievements and performance for this criteria..."
                          disabled={systemStatus !== 'GOALS_ASSIGNED'}
                          value={goal.achievement}
                          onChange={(e) => handleUpdate(goal.id, 'achievement', e.target.value)}
                          className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all disabled:text-slate-400 disabled:bg-slate-100"
                          rows="3"
                        />
                      </div>
                    </div>

                    {/* Manager Comments Section */}
                    <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-bold text-indigo-700 flex items-center gap-2">
                          <ClipboardDocumentCheckIcon className="h-4 w-4 text-indigo-600" />
                          Manager Review
                        </h4>
                        <div>
                          <label className="text-xs font-bold text-indigo-600 uppercase mb-2 block">Manager Rating</label>
                          <select
                            disabled={systemStatus !== 'SELF_SUBMITTED' || userRole !== 'manager'}
                            value={goal.managerRating}
                            onChange={(e) => handleUpdate(goal.id, 'managerRating', e.target.value)}
                            className="w-32 bg-white border border-indigo-200 rounded-lg px-3 py-2 text-sm font-bold focus:ring-2 focus:ring-indigo-500 outline-none disabled:bg-indigo-100 disabled:text-slate-400"
                          >
                            <option value="">Select Rating</option>
                            {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}.0</option>)}
                          </select>
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-xs font-bold text-indigo-600 uppercase mb-2 block">Manager Comments</label>
                        <textarea
                          placeholder={systemStatus !== 'SELF_SUBMITTED' || userRole !== 'manager' ? 'Manager review will be available to manager after employee submission' : 'Manager feedback and comments...'}
                          disabled={systemStatus !== 'SELF_SUBMITTED' || userRole !== 'manager'}
                          value={goal.managerNote}
                          onChange={(e) => handleUpdate(goal.id, 'managerNote', e.target.value)}
                          className="w-full bg-white border border-indigo-200 rounded-lg p-3 text-sm outline-none focus:ring-2 focus:ring-indigo-500 transition-all disabled:text-slate-400 disabled:bg-indigo-100"
                          rows="3"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {systemStatus === 'GOALS_ASSIGNED' && (
                <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end">
                  <button
                    onClick={submitSelfAppraisal}
                    disabled={isSubmitting || goals.some(g => !g.selfRating)}
                    className="bg-slate-900 hover:bg-indigo-600 text-white px-10 py-3 rounded-xl text-xs font-bold uppercase tracking-[0.2em] transition-all shadow-xl flex items-center gap-3 active:scale-95 disabled:opacity-50"
                  >
                    {isSubmitting ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CloudArrowUpIcon className="h-5 w-5" />}
                    Submit Employee Review
                  </button>
                </div>
              )}

              {systemStatus === 'SELF_SUBMITTED' && userRole === 'manager' && (
                <div className="p-6 bg-indigo-50 border-t border-indigo-100 flex justify-end">
                  <button
                    onClick={submitManagerReview}
                    disabled={isSubmitting || goals.some(g => !g.managerRating || !g.managerNote)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-10 py-3 rounded-xl text-xs font-bold uppercase tracking-[0.2em] transition-all shadow-xl flex items-center gap-3 active:scale-95 disabled:opacity-50"
                  >
                    {isSubmitting ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CloudArrowUpIcon className="h-5 w-5" />}
                    Submit Manager Review
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT SIDE: VISUALS & ANALYTICS */}
          <div className="lg:col-span-4 space-y-6">
            {/* Animated Percentage Rings */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
              <h3 className="text-sm font-bold text-slate-700 mb-6 font-heading">Performance Snapshot</h3>

              <div className="grid grid-cols-3 gap-5">
                {/* Ring 1 - Overall Rating */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.75 }}
                  animate={ringsVisible ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.9, ease: "easeOut" }}
                  className="relative flex flex-col items-center"
                >
                  <svg className="w-20 h-20" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" strokeWidth="10" />
                    <motion.circle
                      cx="50" cy="50" r="42" fill="none"
                      stroke="#6366f1" strokeWidth="10" strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: ringsVisible ? 0.84 : 0 }}
                      transition={{ duration: 1.8, ease: "easeOut", delay: 0.3 }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-slate-800">4.2</span>
                    <span className="text-xs text-slate-500">Overall</span>
                  </div>
                </motion.div>

                {/* Ring 2 - Goal Achievement */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.75 }}
                  animate={ringsVisible ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.9, delay: 0.5 }}
                  className="relative flex flex-col items-center"
                >
                  <svg className="w-20 h-20" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" strokeWidth="10" />
                    <motion.circle
                      cx="50" cy="50" r="42" fill="none"
                      stroke="#10b981" strokeWidth="10" strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: ringsVisible ? 0.78 : 0 }}
                      transition={{ duration: 1.8, ease: "easeOut", delay: 0.6 }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-slate-800">78%</span>
                    <span className="text-xs text-slate-500">Goals</span>
                  </div>
                </motion.div>

                {/* Ring 3 - Competency */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.75 }}
                  animate={ringsVisible ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.9, delay: 0.7 }}
                  className="relative flex flex-col items-center"
                >
                  <svg className="w-20 h-20" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" strokeWidth="10" />
                    <motion.circle
                      cx="50" cy="50" r="42" fill="none"
                      stroke="#f59e0b" strokeWidth="10" strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: ringsVisible ? 0.89 : 0 }}
                      transition={{ duration: 1.8, ease: "easeOut", delay: 0.9 }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-slate-800">89%</span>
                    <span className="text-xs text-slate-500">Skills</span>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Medium Bar Chart */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
              <h3 className="text-sm font-bold text-slate-700 mb-4 font-heading">Rating Comparison</h3>
              <div className="h-64">
                <Bar
                  data={barChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'top' },
                    },
                    scales: {
                      y: { beginAtZero: true, max: 5 }
                    }
                  }}
                />
              </div>
            </div>

            {/* Medium Pie Chart */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
              <h3 className="text-sm font-bold text-slate-700 mb-4 font-heading">Weightage Distribution</h3>
              <div className="h-64">
                <Pie
                  data={pieChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'bottom' }
                    }
                  }}
                />
              </div>
            </div>

            {/* FINAL PAYOUT / STATUS (original content) */}
            {systemStatus === 'RELEASED' ? (
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-slate-900 rounded-2xl p-6 text-white shadow-2xl relative overflow-hidden"
              >
                <CheckBadgeIcon className="absolute -right-10 -bottom-10 h-40 w-40 text-white/5" />
                <div className="relative z-10 text-center">
                  <h3 className="text-indigo-400 text-[10px] font-bold uppercase tracking-[0.3em] mb-4">
                    Final Appraisal Release
                  </h3>
                  <div className="flex justify-center items-end gap-2 mb-6">
                    <span className="text-5xl font-black font-heading">{compensation?.hike || 0}%</span>
                    <span className="text-indigo-400 font-bold mb-1">Hike</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-6 text-left">
                    <div className="bg-white/10 p-3 rounded-xl border border-white/10">
                      <p className="text-[9px] text-indigo-300 font-bold uppercase">Bonus</p>
                      <p className="text-md font-bold">${compensation?.bonus || 0}</p>
                    </div>
                    <div className="bg-white/10 p-3 rounded-xl border border-white/10">
                      <p className="text-[9px] text-indigo-300 font-bold uppercase">Rating</p>
                      <p className="text-md font-bold">{goals.length > 0 ? (goals.reduce((sum, g) => sum + (parseFloat(g.managerRating) || 0), 0) / goals.length).toFixed(1) : '0.0'}/5.0</p>
                    </div>
                  </div>
                  <button className="w-full bg-indigo-600 hover:bg-indigo-700 py-3 rounded-xl text-[10px] font-black uppercase flex items-center justify-center gap-2 transition-all">
                    <ArrowDownTrayIcon className="h-4 w-4" /> Download Official Letter
                  </button>
                </div>
              </motion.div>
            ) : (
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm text-center">
                <div className="h-14 w-14 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <InboxStackIcon className="h-6 w-6" />
                </div>
                <h3 className="font-heading font-bold text-slate-800 text-sm">Status Update</h3>
                <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                  {systemStatus === 'GOALS_ASSIGNED'
                    ? "Please fill out your self-ratings and comments to proceed to Manager review."
                    : "Your appraisal is with HR for calibration. Results will be visible shortly."}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceDashboard;